<template>
  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Administration: Benutzer</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">
        Zurück
      </router-link>
    </div>

    <UserProvisioningPanel v-if="userStore.me" ref="historyPanel" @select-user="selectUser({ id: $event })" />
    <div class="row g-3">
      <div class="col-lg-5">
        <div class="card mb-3">
          <div class="card-body">
            <label class="form-label" for="adminUserSearch">Suche</label>
            <div class="input-group">
              <input
                id="adminUserSearch"
                v-model="search"
                class="form-control"
                type="text"
                placeholder="Name, E-Mail ..."
                @keyup.enter="loadUsers"
              />
              <button class="btn btn-outline-secondary" type="button" @click="loadUsers">Suchen</button>
            </div>
          </div>
        </div>

        <div v-if="canInviteUsers" class="card mb-3">
          <div class="card-body">
            <h5 class="card-title">Benutzer einladen</h5>
            <div class="row g-2">
              <div class="col-md-6">
                <label class="form-label" for="inviteFirstName">Vorname</label>
                <input id="inviteFirstName" v-model="inviteForm.firstName" class="form-control" type="text" autocomplete="given-name" />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="inviteLastName">Nachname</label>
                <input id="inviteLastName" v-model="inviteForm.lastName" class="form-control" type="text" autocomplete="family-name" />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="inviteUsername">Benutzername</label>
                <input id="inviteUsername" v-model="inviteForm.username" class="form-control" type="text" autocomplete="username" />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="inviteEmail">E-Mail</label>
                <input id="inviteEmail" v-model="inviteForm.email" class="form-control" type="email" autocomplete="email" />
              </div>
              <div class="col-12">
                <div class="alert alert-info py-2 mb-0">
                  Keycloak sendet eine sichere E-Mail zur Verifizierung und Passwortvergabe. In dieser Anwendung wird kein Passwort gespeichert.
                </div>
              </div>
              <div v-if="userStore.isSystemAdmin" class="col-12">
                <label class="form-label" for="inviteCompany">Firma</label>
                <select id="inviteCompany" v-model="inviteForm.companyId" class="form-select">
                  <option value="">Bitte wählen...</option>
                  <option v-for="c in companies" :key="c.id" :value="c.id">
                    {{ c.name || c.companyNumber || c.id }}
                  </option>
                </select>
              </div>
            </div>

            <div class="mt-3">
              <div class="form-label mb-1">Rollen</div>
              <div class="row g-2">
                <div class="col-md-6" v-for="r in inviteAssignableRoles" :key="`invite-role-${r.id}`">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`invite-role-${r.id}`"
                      :checked="inviteForm.roleIds.includes(r.id)"
                      @change="toggleInviteRole(r.id, $event.target.checked)"
                    />
                    <label class="form-check-label" :for="`invite-role-${r.id}`">{{ r.name }}</label>
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-3">
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="resetInviteForm">Zurücksetzen</button>
              <button class="btn btn-primary btn-sm" type="button" :disabled="!canInviteUsers" @click="createUser">
                Benutzer einladen
              </button>
            </div>
            <div v-if="provisioningStatus" class="alert mt-3 mb-0" :class="provisioningAlertClass" data-testid="provisioning-status">
              <div><strong>{{ provisioningStateLabel }}</strong></div>
              <div v-if="provisioningStatus.failureDetails" class="small">{{ provisioningStatus.failureDetails }}</div>
              <div class="d-flex gap-2 mt-2" v-if="provisioningStatus.isRecoverable || provisioningStatus.state === 'InvitationPending'">
                <button v-if="provisioningStatus.state !== 'InvitationPending'" class="btn btn-outline-primary btn-sm" type="button" @click="retryProvisioning">Erneut versuchen</button>
                <button v-if="provisioningStatus.state === 'InvitationPending'" class="btn btn-outline-primary btn-sm" type="button" @click="resendInvitation">Einladungs-E-Mail erneut senden</button>
              </div>
              <button v-if="provisioningStatus.state !== 'Completed'" class="btn btn-link btn-sm px-0 mt-1" type="button" @click="refreshProvisioningStatus">Status aktualisieren</button>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Benutzer</h5>
            <div class="table-responsive">
              <table class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="u in users"
                    :key="u.id"
                    :class="{ 'table-active': selectedUser?.id === u.id }"
                    style="cursor: pointer"
                    @click="selectUser(u)"
                  >
                    <td>{{ u.displayName || u.preferredUserName || u.id }}</td>
                    <td class="text-truncate" style="max-width: 220px;">{{ u.email || "-" }}</td>
                    <td>
                      <span v-if="!userStore.isSystemAdmin" class="badge text-bg-secondary">Mitglied</span>
                      <span v-else-if="u.isActive" class="badge text-bg-success">App-Zugriff erlaubt</span>
                      <span v-else class="badge text-bg-secondary">App-Zugriff gesperrt</span>
                    </td>
                  </tr>
                  <tr v-if="users.length === 0">
                    <td colspan="3" class="text-center text-muted py-4">Keine Benutzer gefunden.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-7">
        <div v-if="!selectedUser" class="alert alert-secondary">
          Wähle links einen Benutzer aus, um Details zu bearbeiten.
        </div>

        <div v-else class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h5 class="card-title mb-1">{{ selectedUser.displayName || selectedUser.preferredUserName || selectedUser.id }}</h5>
                <div class="text-muted small">{{ selectedUser.email || "-" }}</div>
              </div>
              <div class="d-flex gap-2 align-items-center">
                <span v-if="!userStore.isSystemAdmin" class="badge text-bg-secondary">Mitglied</span>
                <span v-else-if="selectedUser.isActive" class="badge text-bg-success">App-Zugriff erlaubt</span>
                <span v-else class="badge text-bg-secondary">App-Zugriff gesperrt</span>
                <button
                  v-if="userStore.isSystemAdmin"
                  class="btn btn-outline-warning btn-sm"
                  type="button"
                  :disabled="selectedUser.id === userStore.userId"
                  @click="setActiveStatus(!selectedUser.isActive)"
                >
                  {{ selectedUser.isActive ? "App-Zugriff sperren" : "App-Zugriff erlauben" }}
                </button>
                <button class="btn btn-outline-secondary btn-sm" type="button" @click="reloadSelectedUser">Neu laden</button>
              </div>
            </div>

            <hr />

            <div v-if="userStore.isSystemAdmin">
              <h6>Benutzer bearbeiten</h6>
              <div class="row g-2">
                <div class="col-md-6">
                  <label class="form-label" for="profileUsername">Benutzername</label>
                  <input
                    id="profileUsername"
                    class="form-control"
                    type="text"
                    :value="selectedUser.preferredUserName || ''"
                    disabled
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label" for="profileEmail">E-Mail</label>
                  <input id="profileEmail" v-model="profileForm.email" class="form-control" type="email" autocomplete="email" />
                </div>
                <div class="col-md-6">
                  <label class="form-label" for="profileFirstName">Vorname</label>
                  <input id="profileFirstName" v-model="profileForm.firstName" class="form-control" type="text" autocomplete="given-name" />
                </div>
                <div class="col-md-6">
                  <label class="form-label" for="profileLastName">Nachname</label>
                  <input id="profileLastName" v-model="profileForm.lastName" class="form-control" type="text" autocomplete="family-name" />
                </div>
              </div>
              <div class="d-flex justify-content-end mt-3">
                <span class="me-2" role="status">{{ profileNotice }}</span>
                <button class="btn btn-primary btn-sm" type="button" :disabled="!profileDirty" @click="saveProfile">
                  Speichern
                </button>
              </div>
            </div>
            <div v-else class="alert alert-secondary py-2">Identitätsdaten werden von Keycloak verwaltet und können nur durch einen SystemAdmin geändert werden.</div>

            <hr />
            <div>
              <h6>Firmenzuordnung</h6>
              <div class="text-muted small mb-2" v-if="!userStore.isSystemAdmin">
                Als <code>CompanyAdmin</code> kannst du nur die Zuordnung für deine aktuelle Firma ändern.
              </div>
              <div class="row g-2">
                <div class="col-md-6" v-for="c in companies" :key="c.id">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`mem-${c.id}`"
                      :checked="formCompanyIds.includes(c.id)"
                      :disabled="!canEditCompanyMembership(c.id)"
                      @change="toggleCompanyMembership(c.id, $event.target.checked)"
                    />
                    <label class="form-check-label" :for="`mem-${c.id}`">
                      {{ c.name || c.companyNumber || c.id }}
                      <span v-if="c.isDefault" class="badge text-bg-success ms-2">Default</span>
                    </label>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-3">
                <button v-if="selectedUser.companyIds?.includes(userStore.currentCompanyId)" class="btn btn-outline-danger btn-sm me-2" @click="removeCurrentCompany">Aus aktueller Firma entfernen</button>
                <button class="btn btn-primary btn-sm" type="button" :disabled="!companiesDirty" @click="saveCompanies">
                  Speichern
                </button>
              </div>
            </div>

            <hr />

            <div v-if="userStore.isSystemAdmin" data-testid="global-role-assignments">
              <h6>Globale Rollen</h6>
              <div class="row g-2">
                <div class="col-md-6" v-for="r in globalAssignableRoles" :key="r.id">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`grole-${r.id}`"
                      :disabled="selectedUser.id === userStore.userId && r.name === 'SystemAdmin'"
                      :checked="formGlobalRoleIds.includes(r.id)"
                      @change="toggleGlobalRole(r.id, $event.target.checked)"
                    />
                    <label class="form-check-label" :for="`grole-${r.id}`">
                      {{ r.name }}
                      <span v-if="r.description" class="text-muted small ms-2">{{ r.description }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-3">
                <button class="btn btn-primary btn-sm" type="button" :disabled="!globalRolesDirty" @click="saveGlobalRoles">
                  Speichern
                </button>
              </div>
              <hr />
            </div>

            <div data-testid="company-role-assignments">
              <h6>Firmenrollen</h6>
              <div v-if="companyIdsForRoleEditing.length === 0" class="text-muted small">
                Der Benutzer ist aktuell keiner Firma zugeordnet.
              </div>
              <div v-for="companyId in companyIdsForRoleEditing" :key="`cr-${companyId}`" class="mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ companyName(companyId) }}</strong>
                    <span v-if="companyId === userStore.currentCompanyId" class="badge text-bg-info ms-2">Aktuell</span>
                  </div>
                  <button
                    class="btn btn-primary btn-sm"
                    type="button"
                    :disabled="!canEditCompanyRoles(companyId) || !companyRolesDirty(companyId)"
                    @click="saveCompanyRoles(companyId)"
                  >
                    Speichern
                  </button>
                </div>
                <div class="text-muted small" v-if="!canEditCompanyRoles(companyId)">
                  Keine Berechtigung für diese Firma (CompanyAdmin kann nur die aktuelle Firma bearbeiten).
                </div>
                <div class="row g-2 mt-1">
                  <div class="col-md-6" v-for="r in companyAssignableRoles" :key="`cr-${companyId}-${r.id}`">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="`crole-${companyId}-${r.id}`"
                        :checked="(formCompanyRoleIdsByCompanyId[companyId] || []).includes(r.id)"
                        :disabled="!canEditCompanyRoles(companyId)"
                        @change="toggleCompanyRole(companyId, r.id, $event.target.checked)"
                      />
                      <label class="form-check-label" :for="`crole-${companyId}-${r.id}`">{{ r.name }}</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="userStore.isSystemAdmin">
              <hr />
              <h6>Externe Identitäten</h6>
              <div v-if="(selectedUser.externalIdentities || []).length === 0" class="text-muted small mb-2">
                Keine externen Identitäten hinterlegt.
              </div>
              <div class="table-responsive" v-else>
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Issuer</th>
                      <th>Subject</th>
                      <th class="text-end">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="x in selectedUser.externalIdentities" :key="x.id">
                      <td>{{ identityProviderName(x.identityProviderId) }}</td>
                      <td class="text-truncate" style="max-width: 220px;">{{ x.issuer || "-" }}</td>
                      <td class="text-truncate" style="max-width: 240px;"><code>{{ x.subject }}</code></td>
                      <td class="text-end">
                        <button class="btn btn-outline-danger btn-sm" type="button" :disabled="selectedUser.externalIdentities.length < 2" @click="removeExternalIdentity(x)">
                          Entfernen
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="row g-2 align-items-end mt-2">
                <div class="col-md-5">
                  <label class="form-label" for="newExtProvider">Provider</label>
                  <select id="newExtProvider" v-model="newExternal.identityProviderId" class="form-select">
                    <option value="">Bitte wählen...</option>
                    <option v-for="p in identityProviders" :key="p.id" :value="p.id">{{ p.name }}</option>
                  </select>
                </div>
                <div class="col-md-5">
                  <label class="form-label" for="newExtSubject">Subject</label>
                  <input id="newExtSubject" v-model="newExternal.subject" class="form-control" type="text" placeholder="sub / user id" />
                </div>
                <div class="col-md-2">
                  <button class="btn btn-outline-primary w-100" type="button" :disabled="!canAddExternalIdentity" @click="addExternalIdentity">
                    Hinzufügen
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useUxStore } from "@/stores/uxStore";
const uxStore = useUxStore();
import UserProvisioningPanel from "@/components/Admin/UserProvisioningPanel.vue";
import { computed, onMounted, reactive, ref } from "vue";
import AdminUserService from "@/services/AdminUserService";
import AdminRoleService from "@/services/AdminRoleService";
import AdminIdentityProviderService from "@/services/AdminIdentityProviderService";
import CompanyService from "@/services/CompanyService";
import apiClient from "@/axiosConfig";
import { useUserStore } from "@/stores/userStore";

const userStore = useUserStore();

const users = ref([]);
const search = ref("");
const selectedUser = ref(null);
const provisioningStatus = ref(null);
const historyPanel = ref(null);
const profileNotice = ref("");

const companies = ref([]);
const roles = ref([]);
const identityProviders = ref([]);

const GLOBAL_ONLY_ROLE_IDS = new Set([
  "00000000-0000-0000-0000-000000000010",
  "00000000-0000-0000-0000-000000000013",
]);
const GLOBAL_ONLY_ROLE_NAMES = new Set(["systemadmin", "customersupport"]);
const COMPANY_ADMIN_ROLE_ID = "00000000-0000-0000-0000-000000000011";
const USER_ROLE_ID = "00000000-0000-0000-0000-000000000012";
const INVITE_FALLBACK_ROLES = [
  { id: USER_ROLE_ID, name: "User" },
  { id: COMPANY_ADMIN_ROLE_ID, name: "CompanyAdmin" },
];

const inviteForm = reactive({
  username: "",
  email: "",
  firstName: "",
  lastName: "",
  idempotencyKey: "",
  companyId: "",
  roleIds: [],
});

const profileForm = reactive({
  email: "",
  firstName: "",
  lastName: "",
  isActive: true,
});

const formCompanyIds = reactive([]);
const formGlobalRoleIds = reactive([]);
const formCompanyRoleIdsByCompanyId = reactive({});

const original = reactive({
  companyIds: [],
  globalRoleIds: [],
  companyRoleIdsByCompanyId: {},
  profile: { email: "", firstName: "", lastName: "", isActive: true },
});

const newExternal = reactive({
  identityProviderId: "",
  subject: "",
});

function companyName(companyId) {
  return companies.value.find((c) => c.id === companyId)?.name || companyId;
}

function identityProviderName(providerId) {
  return identityProviders.value.find((p) => p.id === providerId)?.name || providerId;
}

function isGlobalOnlyRoleId(roleId) {
  return GLOBAL_ONLY_ROLE_IDS.has(String(roleId || "").toLowerCase());
}

function isGlobalOnlyRole(role) {
  return isGlobalOnlyRoleId(role?.id)
    || GLOBAL_ONLY_ROLE_NAMES.has(String(role?.name || "").toLowerCase());
}

function canEditCompanyMembership(companyId) {
  if (userStore.isSystemAdmin) return true;
  return companyId === userStore.currentCompanyId;
}

function canEditCompanyRoles(companyId) {
  if (userStore.isSystemAdmin) return true;
  return companyId === userStore.currentCompanyId;
}

function snapshotOriginal() {
  original.companyIds = [...formCompanyIds];
  original.globalRoleIds = [...formGlobalRoleIds];
  original.profile = {
    email: profileForm.email,
    firstName: profileForm.firstName,
    lastName: profileForm.lastName,
    isActive: profileForm.isActive,
  };
  original.companyRoleIdsByCompanyId = {};
  Object.keys(formCompanyRoleIdsByCompanyId).forEach((k) => {
    original.companyRoleIdsByCompanyId[k] = [...(formCompanyRoleIdsByCompanyId[k] || [])];
  });
}

function setFormFromSelected() {
  profileForm.email = selectedUser.value?.email || "";
  profileForm.firstName = selectedUser.value?.firstName || splitDisplayName(selectedUser.value?.displayName).firstName;
  profileForm.lastName = selectedUser.value?.lastName || splitDisplayName(selectedUser.value?.displayName).lastName;
  profileForm.isActive = Boolean(selectedUser.value?.isActive);

  formCompanyIds.splice(0, formCompanyIds.length, ...(selectedUser.value?.companyIds || []));
  formGlobalRoleIds.splice(
    0,
    formGlobalRoleIds.length,
    ...(selectedUser.value?.globalRoleIds || []).filter(isGlobalOnlyRoleId)
  );

  Object.keys(formCompanyRoleIdsByCompanyId).forEach((k) => delete formCompanyRoleIdsByCompanyId[k]);
  (selectedUser.value?.companyRoles || []).forEach((cr) => {
    formCompanyRoleIdsByCompanyId[cr.companyId] = (cr.roleIds || []).filter((roleId) =>
      !isGlobalOnlyRoleId(roleId));
  });

  snapshotOriginal();
}

const companiesDirty = computed(() => {
  const a = [...formCompanyIds].sort().join(",");
  const b = [...original.companyIds].sort().join(",");
  return a !== b;
});

const globalRolesDirty = computed(() => {
  const a = [...formGlobalRoleIds].sort().join(",");
  const b = [...original.globalRoleIds].sort().join(",");
  return a !== b;
});

const globalAssignableRoles = computed(() => roles.value.filter(isGlobalOnlyRole));
const companyAssignableRoles = computed(() => roles.value.filter((role) => {
  if (isGlobalOnlyRole(role)) return false;
  return userStore.isSystemAdmin
    || (String(role?.id).toLowerCase() !== COMPANY_ADMIN_ROLE_ID && normalizeRoleName(role?.name) !== "companyadmin");
}));

function normalizeRoleName(roleName) {
  return String(roleName || "").toLowerCase().replace(/[\s_-]/g, "");
}

function isInviteAssignableRole(role) {
  const normalizedRoleName = normalizeRoleName(role?.name);
  if (isGlobalOnlyRole(role)) return false;
  if (!userStore.isSystemAdmin && (String(role?.id).toLowerCase() === COMPANY_ADMIN_ROLE_ID || normalizedRoleName === "companyadmin")) return false;
  return true;
}


const inviteAssignableRoles = computed(() => {
  const byId = new Map();
  roles.value.filter(isInviteAssignableRole).forEach((role) => byId.set(String(role.id).toLowerCase(), role));
  INVITE_FALLBACK_ROLES.filter((role) => userStore.isSystemAdmin || role.id !== COMPANY_ADMIN_ROLE_ID).forEach((role) => {
    if (!byId.has(String(role.id).toLowerCase())) byId.set(String(role.id).toLowerCase(), role);
  });
  return Array.from(byId.values());
});
const canInviteUsers = computed(() => userStore.isSystemAdmin || userStore.isCompanyAdmin);


const profileDirty = computed(() => {
  return profileForm.email !== original.profile.email
    || profileForm.firstName !== original.profile.firstName
    || profileForm.lastName !== original.profile.lastName;
});

function companyRolesDirty(companyId) {
  const current = [...(formCompanyRoleIdsByCompanyId[companyId] || [])].sort().join(",");
  const prev = [...(original.companyRoleIdsByCompanyId[companyId] || [])].sort().join(",");
  return current !== prev;
}

const companyIdsForRoleEditing = computed(() => {
  return (selectedUser.value?.companyIds || []).slice();
});

function splitDisplayName(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/, 2);
  return {
    firstName: parts[0] || "",
    lastName: parts[1] || "",
  };
}


function setDefaultInviteCompany() {
  if (!userStore.isSystemAdmin || inviteForm.companyId) return;

  const currentCompany = companies.value.find((company) => company.id === userStore.currentCompanyId);
  const defaultCompany = companies.value.find((company) => company.isDefault);
  const selectedCompany = currentCompany || defaultCompany || (companies.value.length === 1 ? companies.value[0] : null);
  if (selectedCompany?.id) {
    inviteForm.companyId = selectedCompany.id;
  }
}
function setDefaultInviteRole() {
  if (inviteForm.roleIds.length > 0) return;
  const userRole = inviteAssignableRoles.value.find((role) => String(role.id || "").toLowerCase() === USER_ROLE_ID)
    || inviteAssignableRoles.value.find((role) => normalizeRoleName(role.name) === "user");
  if (userRole?.id) {
    inviteForm.roleIds.push(userRole.id);
  }
}

function resetInviteForm() {
  inviteForm.username = "";
  inviteForm.email = "";
  inviteForm.firstName = "";
  inviteForm.lastName = "";
  inviteForm.idempotencyKey = "";
  inviteForm.companyId = "";
  inviteForm.roleIds.splice(0, inviteForm.roleIds.length);
  setDefaultInviteRole();
}

function toggleInviteRole(roleId, checked) {
  const idx = inviteForm.roleIds.indexOf(roleId);
  if (checked && idx === -1) inviteForm.roleIds.push(roleId);
  if (!checked && idx !== -1) inviteForm.roleIds.splice(idx, 1);
}
function toggleCompanyMembership(companyId, checked) {
  const idx = formCompanyIds.indexOf(companyId);
  if (checked && idx === -1) formCompanyIds.push(companyId);
  if (!checked && idx !== -1) formCompanyIds.splice(idx, 1);

  // Ensure role map exists when membership is added.
  if (checked && !formCompanyRoleIdsByCompanyId[companyId]) {
    formCompanyRoleIdsByCompanyId[companyId] = [];
  }
}

function toggleGlobalRole(roleId, checked) {
  const idx = formGlobalRoleIds.indexOf(roleId);
  if (checked && idx === -1) formGlobalRoleIds.push(roleId);
  if (!checked && idx !== -1) formGlobalRoleIds.splice(idx, 1);
}

function toggleCompanyRole(companyId, roleId, checked) {
  const list = formCompanyRoleIdsByCompanyId[companyId] || [];
  const idx = list.indexOf(roleId);
  if (checked && idx === -1) list.push(roleId);
  if (!checked && idx !== -1) list.splice(idx, 1);
  formCompanyRoleIdsByCompanyId[companyId] = list;
}

async function loadUsers() {
  const response = await AdminUserService.getUsers(search.value || null, 1, 200);
  users.value = Array.isArray(response?.data) ? response.data : [];
}

async function loadSupportingData() {
  const companiesResp = await CompanyService.getAll(null, 1, 200);
  companies.value = Array.isArray(companiesResp?.data) ? companiesResp.data : [];
  setDefaultInviteCompany();

  try {
    const rolesResp = await AdminRoleService.getRoles();
    roles.value = Array.isArray(rolesResp?.data) ? rolesResp.data : [];
  } catch (error) {
    if (error?.response?.data?.code === "administrative_mfa_required") throw error;
    roles.value = [];
  }
  setDefaultInviteRole();

  if (userStore.isSystemAdmin) {
    const idpsResp = await AdminIdentityProviderService.getProviders();
    identityProviders.value = Array.isArray(idpsResp?.data) ? idpsResp.data : [];
  }
}

async function selectUser(user) {
  const response = await AdminUserService.getUserById(user.id);
  selectedUser.value = response?.data || null;
  setFormFromSelected();
}

async function reloadSelectedUser() {
  if (!selectedUser.value?.id) return;
  const response = await AdminUserService.getUserById(selectedUser.value.id);
  selectedUser.value = response?.data || null;
  setFormFromSelected();
}

async function createUser() {
  if (!canInviteUsers.value) return;

  setDefaultInviteRole();
  const payload = {
    username: inviteForm.username.trim(),
    email: inviteForm.email.trim(),
    firstName: inviteForm.firstName.trim(),
    lastName: inviteForm.lastName.trim(),
    roleIds: inviteForm.roleIds.length > 0 ? [...inviteForm.roleIds] : [USER_ROLE_ID],
    isActive: true,
  };
  if (userStore.isSystemAdmin) {
    payload.companyId = inviteForm.companyId || null;
  }

  try {
    inviteForm.idempotencyKey ||= window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    const response = await AdminUserService.createUser(payload, inviteForm.idempotencyKey);

    if (response?.status === 202) {
      provisioningStatus.value = response.data.provisioning;
    } else {
      provisioningStatus.value = response.data.provisioning;
      resetInviteForm();
      await loadUsers();
    }
    await historyPanel.value?.load();
    if (response?.data?.warning) uxStore.setError(response.data.warning);
    if (response?.status === 201 && response?.data?.user?.id) {
      await selectUser(response.data.user);
    }
  } catch (_) {
    // The service already stores the API error message in the UX store.
  }
}

const provisioningStateLabel = computed(() => ({
  Pending: "Wird erstellt",
  KeycloakCreated: "Wird erstellt",
  LocalAssignmentCompleted: "Einladung wird versendet",
  InvitationPending: "Einladung ausstehend",
  Completed: "Abgeschlossen",
  Failed: provisioningStatus.value?.isRecoverable ? "Vorübergehender Fehler" : "Fehlgeschlagen",
})[provisioningStatus.value?.state] || "Wird erstellt");
const provisioningAlertClass = computed(() => provisioningStatus.value?.state === "Completed"
  ? "alert-success"
  : provisioningStatus.value?.state === "Failed" || provisioningStatus.value?.state === "InvitationPending"
    ? "alert-warning" : "alert-info");

async function retryProvisioning() {
  if (!provisioningStatus.value?.id) return;
  const response = await AdminUserService.retryProvisioning(provisioningStatus.value.id);
  provisioningStatus.value = response?.data?.provisioning || response?.data;
  if (provisioningStatus.value?.state === "Completed") await loadUsers();
}

async function refreshProvisioningStatus() {
  if (!provisioningStatus.value?.id) return;
  const response = await AdminUserService.getProvisioningStatus(provisioningStatus.value.id);
  provisioningStatus.value = response?.data;
  if (provisioningStatus.value?.state === "Completed") await loadUsers();
}

async function resendInvitation() {
  if (!provisioningStatus.value?.id) return;
  const response = await AdminUserService.resendInvitation(provisioningStatus.value.id);
  provisioningStatus.value = response?.data?.provisioning || response?.data;
  if (provisioningStatus.value?.state === "Completed") await loadUsers();
}

async function saveProfile() {
  if (!selectedUser.value?.id || !profileDirty.value) return;

  const payload = {
    email: profileForm.email.trim(),
    firstName: profileForm.firstName.trim(),
    lastName: profileForm.lastName.trim(),
  };


  try {
    const response = await AdminUserService.updateUserProfile(selectedUser.value.id, payload);
    profileNotice.value = response.status === 202 ? "Profiländerung vorgemerkt. Den Status finden Sie in der Vorgangsliste." : "Gespeichert.";
    await historyPanel.value?.load();
  } catch (_) {
    // The service already stores the API error message in the UX store.
  }
}
async function setActiveStatus(isActive) {
  if (!selectedUser.value?.id) return;
  if (!userStore.isSystemAdmin) return;
  if (selectedUser.value.id === userStore.userId) return;

  const action = isActive ? "für die Anwendung freigeben" : "für die Anwendung sperren";
  const confirmed = window.confirm(`Benutzer wirklich ${action}?`);
  if (!confirmed) return;

  await AdminUserService.setUserActiveStatus(selectedUser.value.id, isActive);
  await reloadSelectedUser();
  await loadUsers();
}
async function removeCurrentCompany() {
  await apiClient.delete(`/admin/users/${selectedUser.value.id}/companies/${userStore.currentCompanyId}`);
  selectedUser.value = null;
  await loadUsers();
  await historyPanel.value?.load();
}

async function saveCompanies() {
  if (!selectedUser.value?.id) return;
  await AdminUserService.setUserCompanies(selectedUser.value.id, formCompanyIds);
  await reloadSelectedUser();
}

async function saveGlobalRoles() {
  if (!selectedUser.value?.id) return;
  await AdminUserService.setUserGlobalRoles(selectedUser.value.id, formGlobalRoleIds);
  await reloadSelectedUser();
}

async function saveCompanyRoles(companyId) {
  if (!selectedUser.value?.id) return;
  if (!canEditCompanyRoles(companyId)) return;

  const roleIds = formCompanyRoleIdsByCompanyId[companyId] || [];
  await AdminUserService.setUserCompanyRoles(selectedUser.value.id, companyId, roleIds);
  await reloadSelectedUser();
}

const canAddExternalIdentity = computed(() => {
  return userStore.isSystemAdmin && Boolean(newExternal.identityProviderId) && Boolean((newExternal.subject || "").trim());
});

async function addExternalIdentity() {
  if (!selectedUser.value?.id) return;
  const providerId = newExternal.identityProviderId;
  const subject = (newExternal.subject || "").trim();
  await AdminUserService.addExternalIdentity(selectedUser.value.id, providerId, subject);
  newExternal.identityProviderId = "";
  newExternal.subject = "";
  await reloadSelectedUser();
}

async function removeExternalIdentity(identity) {
  const confirmed = window.confirm("Externe Identität wirklich entfernen?");
  if (!confirmed) return;
  await AdminUserService.removeExternalIdentity(identity.id);
  await reloadSelectedUser();
}

onMounted(async () => {
  // Ensure we have a session (used for role-aware UI).
  if (!userStore.me) {
    try {
      await userStore.loadMe();
    } catch (e) {
      console.error("Failed to load /user/me:", e);
    }
  }

  try {
    await loadSupportingData();
    await loadUsers();
  } catch (error) {
    if (error?.response?.data?.code === "administrative_mfa_required") {
      // The interceptor opens the MFA page; this is an expected access state.
      uxStore.clearError();
    } else if (!uxStore.error) {
      uxStore.setError("Die Benutzerverwaltung konnte nicht geladen werden.");
    }
  }
});
</script>



















